import { useCallback, useContext, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { NoticeContext } from 'contexts/notification';
import styles from './index.module.scss';

const delay = 4000;

export type MessageType = 'success' | 'error' | 'warn' | 'info';

export interface DefaultMessageProps {
  message: string;
  id: number;
  type: MessageType;
}

const icons = {
  success: CheckCircle,
  error:   XCircle,
  warn:    AlertCircle,
  info:    Info,
};

const DefaultMessage = ({ message, id, type }: DefaultMessageProps) => {
  const { destroy } = useContext(NoticeContext);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const close = useCallback(() => destroy(id), [destroy, id]);

  useEffect(() => {
    timeout.current = setTimeout(close, delay);
    return () => { if (timeout.current) clearTimeout(timeout.current); };
  }, [close]);

  const Icon = icons[type];

  return (
    <motion.div
      className={`${styles.toast} ${styles[type]}`}
      initial={{ opacity: 0, x: 60, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      layout
    >
      <div className={styles.iconWrap}>
        <Icon size={20} />
      </div>
      <p className={styles.text}>{message}</p>
      <button type='button' className={styles.closeBtn} onClick={close}>
        <X size={16} />
      </button>
      <div className={styles.progress} style={{ animationDuration: `${delay}ms` }} />
    </motion.div>
  );
};

export default DefaultMessage;
